// Image Optimization Service
// Handles image preloading, caching, and optimization across the app

class ImageOptimizationService {
  constructor() {
    this.cache = new Map();
    this.preloadQueue = new Set();
    this.maxConcurrent = 3;
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
    this.loadingPromises = new Map();
    
    // Initialize from localStorage
    this.loadFromStorage();
  }

  // Load cache from localStorage
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('imageCache');
      if (stored) {
        const data = JSON.parse(stored);
        const now = Date.now();
        
        // Only load non-expired items
        Object.entries(data).forEach(([key, value]) => {
          if (now - value.timestamp < this.cacheExpiry) {
            this.cache.set(key, value);
          }
        });
      }
    } catch (error) {
      console.warn('Failed to load image cache from localStorage:', error);
    }
  }

  // Save cache to localStorage
  saveToStorage() {
    try {
      const data = Object.fromEntries(this.cache);
      localStorage.setItem('imageCache', JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save image cache to localStorage:', error);
    }
  }

  // Get optimized image URL
  getOptimizedUrl(originalUrl, options = {}) {
    if (!originalUrl) return originalUrl;
    
    // If it's already a data URL or local image, return as is
    if (originalUrl.startsWith('data:') || originalUrl.startsWith('/')) {
      return originalUrl;
    }

    const { width, height, quality = 80, format = 'webp' } = options;
    
    // For IPFS/Pinata URLs, add optimization parameters
    if (originalUrl.includes('pinata.cloud')) {
      const url = new URL(originalUrl);
      if (width) url.searchParams.set('w', width);
      if (height) url.searchParams.set('h', height);
      url.searchParams.set('q', quality);
      url.searchParams.set('f', format);
      return url.toString();
    }

    return originalUrl;
  }

  // Check if image is cached
  isCached(url) {
    const cached = this.cache.get(url);
    return cached && Date.now() - cached.timestamp < this.cacheExpiry;
  }

  // Get cached image
  getCached(url) {
    const cached = this.cache.get(url);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.src;
    }
    return null;
  }

  // Cache an image
  cacheImage(url, src) {
    this.cache.set(url, {
      src,
      timestamp: Date.now()
    });
    
    // Save to localStorage periodically
    if (this.cache.size % 10 === 0) {
      this.saveToStorage();
    }
  }

  // Preload a single image with optimized timeout
  async preloadImage(url, options = {}) {
    if (this.isCached(url)) {
      return this.getCached(url);
    }

    // Check if already loading
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      // Reduced timeout: 5 seconds instead of 15
      const timeout = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        img.src = ''; // Cancel load
        reject(new Error('Image load timeout'));
      }, 5000);

      img.onload = () => {
        clearTimeout(timeout);
        this.cacheImage(url, url);
        resolve(url);
      };

      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Image load failed'));
      };

      // Add CORS and optimization
      img.crossOrigin = 'anonymous';
      img.src = this.getOptimizedUrl(url, options);
    });

    this.loadingPromises.set(url, promise);
    
    try {
      const result = await promise;
      return result;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  // Preload multiple images with concurrency control
  async preloadImages(urls, options = {}) {
    const validUrls = urls.filter(url => url && !this.isCached(url));
    
    if (validUrls.length === 0) {
      return urls.map(url => this.getCached(url)).filter(Boolean);
    }

    const results = [];
    const chunks = [];
    
    // Split into chunks for concurrency control
    for (let i = 0; i < validUrls.length; i += this.maxConcurrent) {
      chunks.push(validUrls.slice(i, i + this.maxConcurrent));
    }

    // Process chunks sequentially
    for (const chunk of chunks) {
      const chunkResults = await Promise.allSettled(
        chunk.map(url => this.preloadImage(url, options))
      );
      
      results.push(...chunkResults);
    }

    return results.map((result, index) => 
      result.status === 'fulfilled' ? result.value : null
    );
  }

  // Preload critical images (above the fold)
  async preloadCriticalImages(images, viewportHeight = 800) {
    if (!Array.isArray(images) || images.length === 0) return;

    // Prioritize first few images
    const criticalImages = images.slice(0, 3);
    
    try {
      await this.preloadImages(criticalImages, { 
        quality: 90,
        width: 800,
        height: 600
      });
    } catch (error) {
      console.warn('Failed to preload critical images:', error);
    }
  }

  // Preload images for a specific property
  async preloadPropertyImages(property) {
    if (!property?.metaData?.images) return;

    const images = property.metaData.images;
    const imageUrls = images.slice(0, 5); // Preload first 5 images

    try {
      await this.preloadImages(imageUrls, {
        quality: 85,
        width: 600,
        height: 400
      });
    } catch (error) {
      console.warn('Failed to preload property images:', error);
    }
  }

  // Clear expired cache
  clearExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.cacheExpiry) {
        this.cache.delete(key);
      }
    }
    this.saveToStorage();
  }

  // Clear all cache
  clearAllCache() {
    this.cache.clear();
    localStorage.removeItem('imageCache');
  }

  // Get cache stats
  getCacheStats() {
    return {
      size: this.cache.size,
      memoryUsage: JSON.stringify(Object.fromEntries(this.cache)).length,
      preloadQueue: this.preloadQueue.size
    };
  }
}

// Create singleton instance
const imageOptimizationService = new ImageOptimizationService();

// Clean up expired cache on page load
imageOptimizationService.clearExpiredCache();

export default imageOptimizationService;
